import React, { useState, useEffect } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import { FaPencilAlt, FaTrashAlt, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { toastSuccess, toastError, confirmDelete } from "../../utils/alertHelper";
import { hasPermission } from "../../utils/permissionHelper";

// Table columns for BOM
const bomColumns = [
  { key: "srNo", label: "Sr.no:" },
  { key: "name", label: "Name" },
  { key: "code", label: "Code" },
  { key: "createdDate", label: "Created Date" },
  { key: "lastUpdateDate", label: "Last Update Date" },
  { key: "notes", label: "Notes" },
  { key: "actions", label: "Action" },
];

export default function BomIndex() {
  const [searchTerm, setSearchTerm] = useState("");
  const [bomData, setBomData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [businessId, setBusinessId] = useState("");
  const [user, setUser] = useState(null);

  const navigate = useNavigate();

  // User from localStorage (or your auth state management)
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch {}
  }, []);

  // Permission checks
  const canListBOM = hasPermission(user, "ItemBOM", "List");
  const canViewBOM = hasPermission(user, "ItemBOM", "View");
  const canCreateBOM = hasPermission(user, "ItemBOM", "Create");
  const canEditBOM = hasPermission(user, "ItemBOM", "Edit");
  const canDeleteBOM = hasPermission(user, "ItemBOM", "Delete");

  // Get business ID from localStorage
  useEffect(() => {
    const storedBusinessId = localStorage.getItem("businessId");
    if (storedBusinessId) {
      setBusinessId(storedBusinessId);
    } else {
      console.warn("Business ID not found in localStorage");
      toastError("Business ID not found. Please login again.");
    }
  }, []);

  // Fetch BOM data
  useEffect(() => {
    const fetchBomData = async () => {
      if (!businessId || (!canListBOM && !canViewBOM)) return; // Don't fetch if no permission or no businessId

      setLoading(true);
      try {
        const params = {
          page,
          limit: perPage,
          search: searchTerm,
          business: businessId,
        };

        const response = await axiosInstance.get("/api/bom", { params });

        if (response.data && response.data.Status === 1) {
          setBomData(response.data.boms || []);
          setTotal(response.data.totalCount || 0);
        } else {
          setBomData([]);
          setTotal(0);
        }
      } catch (error) {
        console.error("Error fetching BOM data:", error);
        toastError("Failed to fetch BOM data. Please try again.");
        setBomData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchBomData();
  }, [businessId, page, perPage, searchTerm, canListBOM, canViewBOM]);

  // Handle delete BOM
  const handleDelete = async (bomId) => {
    if (!canDeleteBOM) {
      toastError("You do not have permission to delete BOMs.");
      return;
    }
    try {
      const res = await confirmDelete("Are you sure you want to delete this BOM?");
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      await axiosInstance.delete(`/api/bom/${bomId}`);
      toastSuccess("BOM deleted successfully!");
      // Refresh the data
      setBomData(prev => prev.filter(bom => bom._id !== bomId));
      setTotal(prev => prev - 1);
    } catch (error) {
      console.error("Error deleting BOM:", error);
      toastError("Failed to delete BOM. Please try again.");
    }
  };

  // Handle view BOM
  const handleView = (bomId) => {
    if (!canViewBOM) {
      toastError("You do not have permission to view BOMs.");
      return;
    }
    navigate(`/user/bom/view/${bomId}`);
  };

  // Handle edit BOM
  const handleEdit = (bomId) => {
    if (!canEditBOM) {
      toastError("You do not have permission to edit BOMs.");
      return;
    }
    navigate(`/user/bom/edit/${bomId}`);
  };

  // Add actions property to each row
  const tableRows = bomData.map((row, index) => ({
    srNo: (page - 1) * perPage + index + 1,
    name: row.name || "",
    code: row.code || "",
    createdDate: row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB') : "",
    lastUpdateDate: row.updatedAt ? new Date(row.updatedAt).toLocaleDateString('en-GB') : "",
    notes: row.note || "",
    actions: (
      <div className="d-flex gap-2">
        {canEditBOM && (
          <FaPencilAlt
            title="Edit"
            style={{
              color: "#3F3F46",
              fontSize: "16px",
              cursor: "pointer"
            }}
            onClick={() => handleEdit(row._id)}
          />
        )}
        {canDeleteBOM && (
          <FaTrashAlt
            title="Delete"
            style={{
              color: "#ec0f0fff",
              fontSize: "16px",
              cursor: "pointer"
            }}
            onClick={() => handleDelete(row._id)}
          />
        )}
      </div>
    ),
  }));

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
        <h2 className="fw-bold" style={{ fontSize: "2rem" }}>BOM</h2>
        {canCreateBOM && (
          <button
            className="btn btn-primary"
            onClick={() => navigate("/user/bom/create")}
          >
            + Add New BOM
          </button>
        )}
      </div>
      <div className="row mb-4">
        <div className="col-md-6"></div>
        <div className="col-md-6 d-flex justify-content-end">
          <div className="input-group" style={{ maxWidth: 280, borderRadius: "6px", overflow: "hidden" }}>
            <span className="input-group-text bg-white border-end-0" style={{ borderRadius: "6px 0 0 6px" }}>
              <i className="fas fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ boxShadow: "none", borderRadius: "0 6px 6px 0" }}
            />
          </div>
        </div>
      </div>
      <CommonTable
        columns={bomColumns}
        data={tableRows}
        loading={loading}
        itemsPerPageOptions={[10, 15, 25, 50]}
        defaultItemsPerPage={10}
        bordered
        pagination={{
          page,
          perPage,
          total,
          onChangePage: setPage,
          onChangePerPage: setPerPage,
        }}
      />
    </div>
  );
}